/**
 * AlphaSound — sonidos de la plataforma, sintetizados con WebAudio (sin assets).
 * Discretos y premium: pings cortos, acordes suaves. Silenciable globalmente
 * (persistido en localStorage) — ver el botón de sonido en el Cielo Alpha.
 */

import { CANAL, canalesDe, estaEnSilencio, suena } from "./canalesDeSonido.mjs";
import { DEL_JUEGO } from "./sonidosDelJuego.mjs";
import { DEL_TALLER } from "./sonidosDeLaFragua.mjs";
import { contexto } from "./audioDeLaCasa.mjs";
import { precarga, suena as laMuestra } from "./bancoDeSonidos.mjs";
import { MUESTRAS } from "./muestras.mjs";
import { lee } from "./preferencias.mjs";
import { vozDeAlpha, vozDeSocrates } from "./voces.js";
import { ceremoniaDeApertura } from "./apertura.mjs";
import { compuerta, despegue, ruido } from "./sonidosDelMundo.js";
import * as delRepaso from "./vocesDelRepaso.js";

class AlphaSound {
  constructor() {
    this.ctx = null;
    // TRES CANALES Y NO UN BOOLEANO. Aquí había un `muted` único que apagaba todo lo que
    // hace ruido, y con él se iba también el lector de voz de las actividades: quien se
    // satura con la fanfarria tenía que elegir entre eso y su ayuda de accesibilidad.
    //
    // Y NO SE GUARDA NADA AQUI: manda el ajuste «Sonido» de la pantalla de Ajustes, que ya
    // existía con estas tres opciones exactas y no estaba conectado a nada. Esto lo lee al
    // arrancar -antes de que React monte, para que el primer sonido ya sea el correcto- y
    // `usePreferencias` lo vuelve a empujar cada vez que cambia.
    this.canales = canalesDe(this._leeAjuste());
  }

  _leeAjuste() {
    try { return lee(window.localStorage).sonido; } catch { return undefined; }
  }

  /** Lo que el alumno tiene puesto en Ajustes. Lo llama `usePreferencias` al cambiar. */
  aplica(ajuste) {
    this.canales = canalesDe(ajuste);
  }

  /** Si un sonido concreto puede sonar. Cada método lo pregunta por su nombre. Y si ese nombre
   *  tiene fichero, lo dispara el banco y aquí se contesta que no: lo de abajo no se sintetiza. */
  _suena(nombre) {
    if (laMuestra(nombre, { estado: this.canales })) return false;
    return suena(nombre, this.canales);
  }

  /**
   * Si un canal entero está encendido.
   *
   * Lo preguntan los que suenan por su cuenta y no por aquí —el lector de voz, las dos músicas
   * y el fuego—: no pueden llevar la guarda de los demás, pero el interruptor tiene que ser el
   * mismo. **La decisión de si algo suena vive en un sitio, aunque lo produzcan tres cacharros
   * distintos.** Y no vale `isMuted()`, que quiere decir «los dos canales de ruido apagados»:
   * con eso, apagar sólo la fanfarria dejaba la música sonando.
   */
  puede(canal) {
    return this.canales[canal] !== false;
  }

  /** El estado de los tres, para quien tenga que pintarlo. */
  estado() {
    return { ...this.canales };
  }

  _ensure() {
    // UNO PARA TODA LA CASA. Aquí se abría el suyo, y con él eran cinco contextos de audio
    // distintos en la misma página: cinco hilos pidiendo sonido a la vez en una máquina que
    // además dibuja un mundo 3D. El porqué entero —y el techo de Chrome, que es lo que deja
    // todo mudo de golpe— está en `lib/audioDeLaCasa.mjs`.
    const primera = !this.ctx;
    this.ctx = contexto(window);
    // Y AL ABRIRSE SE TRAEN LAS QUE TIENEN FICHERO: si no, la primera vez suena el oscilador de
    // reserva y la segunda el fichero, dos sonidos para un botón (Gabri, 27 de octubre de 2026).
    if (primera && this.ctx) {
      precarga(Object.keys(MUESTRAS).filter((k) => typeof this[k] === "function"));
    }
    return this.ctx;
  }

  /**
   * Si la casa está callada. Lo usa el botón del Cielo para pintarse.
   *
   * Ya NO existe un `setMuted`: el botón escribe el ajuste, como lo escribe la pantalla de
   * Ajustes, y desde ahí baja. Dos mandos para lo mismo con dos almacenes distintos es
   * exactamente lo que este cambio venía a quitar, y tenerlo aquí lo habría vuelto a poner.
   */
  isMuted() {
    return estaEnSilencio(this.canales);
  }

  _tone({ freq = 660, to = null, type = "sine", dur = 0.15, gain = 0.12, when = 0 }) {
    const ctx = this._ensure();
    if (!ctx) return;
    const t0 = ctx.currentTime + when;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (to) osc.frequency.exponentialRampToValueAtTime(to, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  }

  /** micro-click de interfaz (hover/selección de opciones) */
  tick() {
    if (!this._suena("tick")) return;
    this._tone({ freq: 880, type: "sine", dur: 0.05, gain: 0.06 });
  }

  /** seleccionar una estrella */
  ping() {
    if (!this._suena("ping")) return;
    this._tone({ freq: 620, to: 990, type: "triangle", dur: 0.14, gain: 0.1 });
  }

  /** acción no disponible (estrella bloqueada) */
  deny() {
    if (!this._suena("deny")) return;
    this._tone({ freq: 220, to: 160, type: "square", dur: 0.12, gain: 0.05 });
  }

  /** nueva estrella revelada */
  chime() {
    if (!this._suena("chime")) return;
    this._tone({ freq: 659.25, dur: 0.22, gain: 0.09 });          // E5
    this._tone({ freq: 987.77, dur: 0.3, gain: 0.08, when: 0.09 }); // B5
  }

  /** estrella completada: arpegio ascendente */
  arpeggio() {
    if (!this._suena("arpeggio")) return;
    this._tone({ freq: 440.0, dur: 0.16, gain: 0.1 });             // A4
    this._tone({ freq: 554.37, dur: 0.16, gain: 0.1, when: 0.1 }); // C#5
    this._tone({ freq: 659.25, dur: 0.3, gain: 0.11, when: 0.2 }); // E5
  }

  /** La voz de Alpha: unas sílabas sin palabras. La receta, en `lib/voces.js`. */
  balbucea(semilla) {
    if (!this._suena("balbucea")) return;
    vozDeAlpha(this, semilla);
  }

  /** desplazamiento del Viajero */
  whoosh() {
    if (!this._suena("whoosh")) return;
    ruido(this._ensure(), { dur: 0.35, tipo: "bandpass", desde: 400, hasta: 1600, gain: 0.08 });
  }

  /** Los tres del taller: el golpe, la pieza que se parte y la palabra acabada al agua del
   *  pilon. Van por el generador de ruido de la casa, que ya existia; las recetas, en
   *  `lib/sonidosDeLaFragua.mjs`. */
  _delTaller(cual, volumen = 1) {
    if (this._suena(cual)) ruido(this._ensure(), { ...DEL_TALLER[cual], gain: DEL_TALLER[cual].gain * volumen });
  }

  /** `volumen` lo usa el martillo de Max (M25), que se oye según lo cerca que estés. */
  martillo(volumen = 1) { this._delTaller("martillo", volumen); }
  chisporroteo() { this._delTaller("chisporroteo"); }
  temple() { this._delTaller("temple"); }

  // --- los cofres -------------------------------------------------------------
  // Tres sonidos, no un modulo: las primitivas y el mute ya estaban aqui.
  cofreEmpieza() {
    if (!this._suena("cofreEmpieza")) return;
    // Dos tonos ascendentes y secos: «esto ya corre». Corto a proposito, porque se oye
    // cada vez que el alumno arranca una llave y un sonido largo cansa antes.
    this._tone({ freq: 392, to: 523, type: "triangle", dur: 0.14, gain: 0.05 });
    this._tone({ freq: 523, to: 659, type: "triangle", dur: 0.16, gain: 0.05, when: 0.1 });
  }

  cofreListo() {
    if (!this._suena("cofreListo")) return;
    // Campanilla de dos notas, discreta. Quien lo llama tiene que asegurarse de que suena
    // UNA VEZ POR COFRE: un aviso que se repite en cada render se convierte en ruido y el
    // alumno silencia el juego entero, y entonces se pierden tambien los que si queria.
    this._tone({ freq: 880, to: 880, type: "sine", dur: 0.5, gain: 0.05 });
    this._tone({ freq: 1174, to: 1174, type: "sine", dur: 0.6, gain: 0.04, when: 0.12 });
  }

  cofreRedoble(dur = 0.7) {
    if (!this._suena("cofreRedoble")) return;
    // Ruido filtrado que sube justo antes de la revelacion. Es lo que convierte una
    // animacion en un momento: sin el, la tapa se abre y ya esta.
    this._noise(dur, 300, 0.04, "bandpass");
    this._tone({ freq: 196, to: 392, type: "sawtooth", dur, gain: 0.03 });
  }

  fanfare() {
    if (!this._suena("fanfare")) return;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
      this._tone({ freq: f, dur: 0.35, gain: 0.13, when: i * 0.12 })
    );
  }

  /** golpe retro 8-bit (batalla del Jefe) */
  hit8bit() {
    if (!this._suena("hit8bit")) return;
    this._tone({ freq: 320, to: 120, type: "square", dur: 0.08, gain: 0.06 });
    this._tone({ freq: 90, type: "square", dur: 0.1, gain: 0.05, when: 0.02 });
  }

  /** daño recibido 8-bit */
  hurt8bit() {
    if (!this._suena("hurt8bit")) return;
    this._tone({ freq: 160, to: 70, type: "square", dur: 0.16, gain: 0.06 });
  }

  /** ráfaga de ruido filtrado (base para SFX cómicos) */
  _noise(dur = 0.15, freq = 1000, gain = 0.06, filterType = "bandpass") {
    ruido(this._ensure(), { dur, desde: freq, gain, tipo: filterType });
  }

  /* ---- SFX cómicos de la batalla slapstick ---- */
  /** ¡PUNCH! de cómic (librazo/zapatillazo) */
  punch() {
    if (!this._suena("punch")) return;
    this._tone({ freq: 150, to: 55, type: "square", dur: 0.12, gain: 0.1 });
    this._noise(0.07, 1400, 0.07, "lowpass");
  }
  /**
   * ¡BOING! elástico. Es el salto de la Estrella, y **ya estaba escrito y no lo llamaba nadie**:
   * un sonido construido, probado y sin forma de llegar, que es la avería de esta casa aplicada
   * a dos líneas de audio. El día que el cono rojo de debajo pasó de adorno a muelle, resultó
   * que su sonido llevaba meses esperando.
   *
   * Y ES NUESTRO, hecho con dos senos. La idea venía de «un sonido tipo Toad o Mario Bros», y
   * los de Nintendo no se copian: además de ser suyos, un sample pegado desentonaría con todo
   * lo demás, que en esta casa se sintetiza en código.
   */
  boing() {
    if (!this._suena("boing")) return;
    // TRES BOTES QUE SE APAGAN, y no dos: Gabri pidió el 27 de octubre de 2026 *«más fuerte o con
    // sensación de más rebote»*, y el rebote se oye en que cada bote es más corto y más flojo.
    this._tone({ freq: 560, to: 160, type: "sine", dur: 0.16, gain: 0.16 });
    this._tone({ freq: 180, to: 520, type: "sine", dur: 0.13, gain: 0.11, when: 0.13 });
    this._tone({ freq: 420, to: 200, type: "sine", dur: 0.09, gain: 0.07, when: 0.25 });
    this._tone({ freq: 260, to: 440, type: "triangle", dur: 0.07, gain: 0.04, when: 0.33 });
  }
  /**
   * EL TOBOGAN del viaducto. Roce de piedra que sube de tono conforme se coge carrerilla, y una
   * nota baja debajo para que tenga cuerpo. Suena UNA VEZ, al empezar a deslizarse: quien lo
   * llame en cada cuadro lo convierte en un zumbido.
   *
   * Y NO ES EL `whoosh`, aunque se parezca. Aquel es del canal de INTERFAZ -confirma que lo que
   * has hecho ha llegado- y esto es del de JUEGO. Reusarlo habria metido un sonido de juego en
   * el canal que no se puede apagar sin perder las ayudas, que es justo lo que los tres canales
   * existen para impedir.
   */
  tobogan() {
    if (!this._suena("tobogan")) return;
    this._noise(0.9, 900, 0.05, "bandpass");
    this._tone({ freq: 220, to: 140, type: "sine", dur: 0.8, gain: 0.035 });
  }

  /** zapatillazo: chasquido seco */
  thwack() {
    if (!this._suena("thwack")) return;
    this._tone({ freq: 220, to: 70, type: "square", dur: 0.07, gain: 0.09 });
    this._noise(0.05, 2200, 0.07, "bandpass");
  }

  /** Las tres del repaso. Qué hace sonar cada una, en `lib/vocesDelRepaso.js`. */
  repasada() { delRepaso.repasada(this); }
  oooh() { delRepaso.oooh(this); }
  guardada() { delRepaso.guardada(this); }

  /** Los cuatro de un minijuego. Las notas y el porqué, en `lib/sonidosDelJuego.mjs`. */
  _delJuego(cual) { if (this._suena(cual)) DEL_JUEGO[cual].forEach((n) => this._tone(n)); }

  acierto() { this._delJuego("acierto"); }
  fallo() { this._delJuego("fallo"); }
  ganas() { this._delJuego("ganas"); }
  pierdes() { this._delJuego("pierdes"); }
  /** Y las dos voces del encuentro con Asterión: mismo banco, y no son de los cuatro. */
  rugido() { this._delJuego("rugido"); }
  susto() { this._delJuego("susto"); }

  /**
   * EL UMBRAL: cruzar del Ágora al Mundo Alpha. Suena al pulsar la puerta de la cabecera.
   *
   * POR QUÉ EXISTE. Lo pidió Gabri el 14 de septiembre de 2026 —«que al pulsar haga algún sonido
   * memorable»— y es la única puerta que hay al mundo 3D. Un `tick` de interfaz diría «te he
   * oído»; aquí hace falta que diga **«esto es otro sitio»**, que es lo que el clic significa.
   *
   * CÓMO ESTÁ HECHO: una campana grave golpeada —fundamental, quinta y octava, que es lo que
   * suena al golpear algo grande y hueco— con cola larga, y encima un barrido que sube, que es
   * la puerta abriéndose. El aire del principio es ruido filtrado: sin él la campana empieza de
   * la nada y suena a sintetizador. Va en el canal de JUEGO y no en el de interfaz: no confirma
   * nada que no confirme ya el cambio de pantalla, y en INTERFAZ sólo va lo que informa.
   */
  umbral() {
    if (!this._suena("umbral")) return;
    // La campana: 110 Hz, su quinta y su octava, con la cola cada vez más corta hacia arriba.
    this._tone({ freq: 110, type: "sine", dur: 1.9, gain: 0.085 });
    this._tone({ freq: 164.81, type: "sine", dur: 1.5, gain: 0.05, when: 0.02 });
    this._tone({ freq: 220, type: "sine", dur: 1.2, gain: 0.04, when: 0.04 });
    // La puerta que se abre: sube y se queda arriba.
    this._tone({ freq: 220, to: 880, type: "triangle", dur: 0.55, gain: 0.045, when: 0.06 });
    // Y el aire de antes de la campana.
    this._noise(0.35, 700, 0.05, "lowpass");
  }


  /** Y su «mmm» o su «uuh» al contestarle en el tranvía: `lib/vozDeSocrates.mjs`. */
  socratesDice(cual) {
    if (!this._suena("socratesDice")) return;
    vozDeSocrates(this, cual);
  }

  /** La cabina de una nave amarrada, al preguntar si despegas: la compuerta. */
  naveCabina() {
    if (!this._suena("naveCabina")) return;
    compuerta(this);
  }

  /** Los motores de la nave, al elegir rumbo. */
  naveDespega() {
    if (!this._suena("naveDespega")) return;
    despegue(this);
  }

  /** teletransporte del ascensor de luz. down=true = descenso (barrido inverso). */
  teleport(down = false) {
    if (!this._suena("teleport")) return;
    const ctx = this._ensure();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    // LA SALA: todo pasa por aquí y sale seco y con eco. Gabri, 27 de octubre de 2026: *«un poco
    // más de reverb y volumen»*. Un retardo que se realimenta por un filtro bajo es la reverb
    // barata de siempre, y no pide cargar ningún fichero de impulso.
    const sala = ctx.createGain();
    const eco = ctx.createDelay(1); eco.delayTime.value = 0.13;
    const vuelta = ctx.createGain(); vuelta.gain.value = 0.48;
    const oscuro = ctx.createBiquadFilter(); oscuro.type = "lowpass"; oscuro.frequency.value = 2400;
    sala.connect(ctx.destination);
    sala.connect(eco).connect(oscuro).connect(vuelta).connect(eco);
    oscuro.connect(ctx.destination);
    const sweep = (type, f0, f1, dur, gain) => {
      const o = ctx.createOscillator(); o.type = type;
      o.frequency.setValueAtTime(f0, t0);
      o.frequency.exponentialRampToValueAtTime(f1, t0 + dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(gain, t0 + 0.12);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur + 0.1);
      o.connect(g).connect(sala); o.start(t0); o.stop(t0 + dur + 0.15);
    };
    if (down) {
      sweep("sine", 1400, 180, 1.1, 0.14);
      sweep("triangle", 2100, 260, 1.1, 0.08);
    } else {
      sweep("sine", 180, 1400, 1.1, 0.14);
      sweep("triangle", 270, 2100, 1.1, 0.08);
    }
    // shimmer de ruido con paso de banda (ascendente o descendente)
    const dur = 1.2, size = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, size, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < size; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 6;
    bp.frequency.setValueAtTime(down ? 4200 : 600, t0);
    bp.frequency.exponentialRampToValueAtTime(down ? 600 : 4200, t0 + 1.1);
    const gn = ctx.createGain();
    gn.gain.setValueAtTime(0.07, t0);
    gn.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(bp).connect(gn).connect(sala); src.start(t0);
  }

  /** La ceremonia de abrir un sitio. Los tres golpes y su porqué, en `lib/apertura.mjs`. */
  seAbre() {
    if (!this._suena("seAbre")) return;
    ceremoniaDeApertura(this);
  }

  /** jingle de victoria chiptune (estilo "level clear" de Game Boy) */
  victory8bit() {
    if (!this._suena("victory8bit")) return;
    const sq = (freq, when, dur = 0.13, gain = 0.07) => this._tone({ freq, type: "square", dur, gain, when });
    const mel = [
      [392.0, 0.0], [523.25, 0.13], [659.25, 0.26], [783.99, 0.39],
      [1046.5, 0.52, 0.2], [783.99, 0.74, 0.1], [1046.5, 0.86, 0.42, 0.08],
    ];
    mel.forEach(([f, w, d = 0.13, g = 0.07]) => sq(f, w, d, g));
    // bajo cuadrado
    [130.81, 164.81, 196.0, 261.63].forEach((f, i) =>
      this._tone({ freq: f, type: "square", dur: 0.18, gain: 0.035, when: i * 0.18 })
    );
  }
}

export const sound = new AlphaSound();
